import React, { useEffect, useState } from "react";
import { useUtil } from "../../../contexts/UtilContext";
import Navbar from "../../../components/Navbar/Navbar";

import styles from "./DashAddAgency.module.css";
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Footer from "../../../components/Footer/Footer";
import {
  isValidEmail,
  isValidPhone,
  sendEmailVerification,
} from "../../../util/utilFunctions";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { stateOptions } from "../../../util/formElements";
import LanguageSelector from "../../../components/LanguageSelector";

// Styled components for centering modal elements
const CenteredDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": { textAlign: "center" },
}));
const CenteredDialogTitle = styled(DialogTitle)({ textAlign: "center" });
const CenteredDialogContent = styled(DialogContent)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});
const CenteredDialogActions = styled(DialogActions)({
  justifyContent: "center",
  padding: "16px",
});

// Factory for a new license row
const newLicense = () => ({
  licenseNumber: "",
  licenseType: "",
  issuingState: "",
  issuedDate: null,
  expiryDate: null,
  status: "Active",
  documentUrl: null,
  file: null,
  fileName: "",
});

function DashAddAgency() {
  const { accessToken } = useAuth();
  const { showLoader, hideLoader } = useUtil();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [timer, setTimer] = useState(0);
  const [showResendButton, setShowResendButton] = useState(false);
  const [openExistingAgencyModal, setOpenExistingAgencyModal] = useState(false);

  const [emailDetails, setEmailDetails] = useState({ email: "" });
  const [basicDetails, setBasicDetails] = useState({
    name: "",
    bio: "",
    phone: "",
    // licenseNumber REMOVED from here (each license now has its own number)
    logoUrl: null,
  });
  const [addressDetails, setAddressDetails] = useState({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipcode: "",
    landmark: "",
  });
  const [professionalDetails, setProfessionalDetails] = useState({
    establishedYear: "",
    languages: "",
    specialization: "",
    linkedinLink: "",
    instagramLink: "",
    youtubeLink: "",
    tiktokLink: "",
  });
  const [verificationDetails, setVerificationDetails] = useState({
    verificationCode: "",
  });

  // --- Logo upload state ---
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);

  // --- Multi-license state ---
  const [licenseList, setLicenseList] = useState([newLicense()]);

  const specializations = [
    "Residential",
    "Commercial",
    "Investment",
    "Specialty Properties",
    "Real Estate Development",
    "Real Estate Management",
    "Distressed Properties",
    "Real Estate Law and Consulting",
    "Green and Sustainable Real Estate",
    "International",
  ];

  const handleExistingAgencyModalClose = () =>
    setOpenExistingAgencyModal(false);
  const handleUserAnotherEmail = () => {
    setEmailDetails({ email: "" });
    setOpenExistingAgencyModal(false);
  };
  const handleDashAgenciesRedirect = () =>
    navigate(`/MyFirstLand?category=agencies`);

  const startTimer = () => {
    setTimer(60);
    setShowResendButton(false);
  };

  // specialization dialog
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // field handlers
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailDetails({ ...emailDetails, [name]: value });
  };
  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicDetails({ ...basicDetails, [name]: value });
  };
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressDetails({ ...addressDetails, [name]: value });
  };
  const handleEstablishedYearChange = (date) => {
    setProfessionalDetails((prev) => ({
      ...prev,
      establishedYear: date ? dayjs(date) : null,
    }));
  };
  const handleProfessionalChange = (e) => {
    const { name, value } = e.target;
    if (name === "specialization") {
      const specializationString = Array.isArray(value)
        ? value.join(", ")
        : value;
      setProfessionalDetails((prev) => ({
        ...prev,
        [name]: specializationString,
      }));
    } else if (name === "languages") {
      const languageString = Array.isArray(value) ? value.join(", ") : value;
      setProfessionalDetails((prev) => ({ ...prev, [name]: languageString }));
    } else {
      setProfessionalDetails((prev) => ({ ...prev, [name]: value }));
    }
  };
  const handleVerificationChange = (e) => {
    const { name, value } = e.target;
    setVerificationDetails({ ...verificationDetails, [name]: value });
  };

  // ---------- Logo helpers ----------
  const validateLogo = (file) => {
    const maxSizeMB = 2;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Logo must be PNG, JPG, or WEBP.");
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Logo must be less than ${maxSizeMB}MB.`);
      return false;
    }
    return true;
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateLogo(file)) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async (agencyId) => {
    if (!logoFile) return null;

    const form = new FormData();
    form.append("logo", logoFile); // must match backend upload.single("logo")
    form.append("agencyId", agencyId);

    const res = await axios.post(
      `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/upload-logo`,
      form,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (!res.data?.success || !res.data?.url) {
      throw new Error(res.data?.message || "Logo upload failed");
    }

    setLogoUrl(res.data.url);
    return res.data.url;
  };

  // ---------- Multi-license helpers ----------
  const addLicense = () => setLicenseList((prev) => [...prev, newLicense()]);
  const removeLicense = (index) =>
    setLicenseList((prev) => prev.filter((_, i) => i !== index));

  const handleLicenseField = (index, e) => {
    const { name, value } = e.target;
    setLicenseList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [name]: value };
      return copy;
    });
  };

  const handleIssuedDateChange = (index, date) => {
    setLicenseList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], issuedDate: date ? dayjs(date) : null };
      return copy;
    });
  };

  const handleExpiryDateChange = (index, date) => {
    setLicenseList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], expiryDate: date ? dayjs(date) : null };
      return copy;
    });
  };

  const onLicenseFileChange = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "application/pdf",
    ];
    const maxSizeMB = 5;
    if (!okTypes.includes(file.type)) {
      toast.error("License doc must be PNG, JPG, WEBP, or PDF.");
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`License doc must be less than ${maxSizeMB}MB.`);
      return;
    }
    setLicenseList((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], file, fileName: file.name };
      return copy;
    });
  };

  const uploadLicenseDocument = async (agencyId, agencyLicenseId, file) => {
    if (!file) return null;

    const form = new FormData();
    form.append("licenseDocument", file);
    form.append("agencyId", agencyId);
    if (agencyLicenseId) form.append("agencyLicenseId", agencyLicenseId);

    const res = await axios.post(
      `${
        import.meta.env.VITE_BACKEND_URL
      }/dash/pro-agencies/upload-license-document`,
      form,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (!res.data?.success || !res.data?.url) {
      throw new Error(res.data?.message || "License document upload failed");
    }

    return res.data.url;
  };

  // ---------- Next / Back flow ----------
  const handleNext = async () => {
    if (step === 1) {
      if (validateEmailDetails()) {
        const response = await verifyAgencyEmail(emailDetails.email);
        if (
          response?.status === 200 &&
          response.data &&
          "success" in response.data
        ) {
          if (response.data.emailAsUser || response.data.emailAsAgency) {
            setOpenExistingAgencyModal(true);
          } else {
            setStep(step + 1);
          }
        } else {
          toast.error(
            "Some error occurred while verifying the entered email. Please try again!"
          );
        }
      }
    } else if (step === 2) {
      if (validateBasicDetails() && validateAllLicenses()) {
        setStep(step + 1);
      }
    } else if (step === 3) {
      if (validateAddressDetails()) {
        setStep(step + 1);
      }
    } else if (step === 4) {
      if (validateProfessionalDetails()) {
        try {
          showLoader();
          const response = await sendEmailVerification(
            emailDetails.email,
            true
          );
          if (
            response.status === 200 &&
            response.data &&
            "success" in response.data
          ) {
            setStep(step + 1);
            startTimer();
          } else {
            toast.error(
              "Some error occurred while sending verification email. Please try again!"
            );
          }
        } catch (error) {
          console.error(
            "Error while trying to send verification email: ",
            error
          );
          toast.error(
            "Some error occurred while sending verification email. Please try again!"
          );
        } finally {
          hideLoader();
        }
      }
    }
  };

  const handleBack = () => setStep(step - 1);

  // ---------- Submit (Step 5) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateVerificationDetails()) return;

    try {
      showLoader();

      // Build licenses payload
      const licensesPayload = licenseList.map((lic) => ({
        licenseNumber: lic.licenseNumber,
        licenseType: lic.licenseType || null,
        issuingState: lic.issuingState,
        issuedDate: lic.issuedDate
          ? lic.issuedDate.toDate().toISOString()
          : null,
        expiryDate: lic.expiryDate
          ? lic.expiryDate.toDate().toISOString()
          : null,
        status: lic.status || "Active",
        license_document_url: null, // set after upload
      }));

      const requestData = {
        emailDetails,
        // logoUrl is updated by the upload endpoint; keep payload lean:
        basicDetails: { ...basicDetails, logoUrl: null },
        addressDetails,
        professionalDetails: {
          ...professionalDetails,
          establishedYear: professionalDetails.establishedYear
            ? professionalDetails.establishedYear.toDate().toISOString()
            : null,
        },
        verificationDetails,
        licenseDetails: licensesPayload, // ARRAY
      };

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/create`,
        requestData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (
        response.status === 200 &&
        response.data &&
        "success" in response.data
      ) {
        if (response.data.success) {
          const { agencyId, agencyLicenseIds } = response.data;

          // 1) Upload logo (non-blocking to the main flow)
          if (logoFile) {
            try {
              await uploadLogo(agencyId);
            } catch (err) {
              console.error("Logo upload failed:", err);
            }
          }

          // 2) Bulk upload license docs (only for licenses that have files)
          if (Array.isArray(agencyLicenseIds)) {
            const pairs = [];
            for (let i = 0; i < licenseList.length; i++) {
              const file = licenseList[i].file;
              if (!file) continue;
              const licenseId = agencyLicenseIds[i];
              if (!licenseId) {
                console.warn(
                  `Missing agencyLicenseId for license index ${i}; skipping file.`
                );
                continue;
              }
              pairs.push({ licenseId, file });
            }

            if (pairs.length) {
              const form = new FormData();
              form.append("agencyId", agencyId);

              // Align items and files by index
              const items = pairs.map((p) => ({ licenseId: p.licenseId }));
              form.append("items", JSON.stringify(items));
              for (const p of pairs) {
                form.append("licenseDocuments", p.file);
              }

              try {
                const res = await axios.post(
                  `${
                    import.meta.env.VITE_BACKEND_URL
                  }/dash/agency-licenses/bulk-upload-documents`,
                  form,
                  {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                      // Let the browser set multipart boundary
                    },
                  }
                );

                if (!res?.data?.success) {
                  console.warn(
                    "Bulk license upload returned non-success:",
                    res?.data
                  );
                } else {
                  // Optional: reflect uploaded URLs in local state using res.data.licenses
                }
              } catch (e) {
                console.error("Bulk license upload failed:", e);
              }
            }
          } else {
            console.warn(
              "agencyLicenseIds not returned; skipping license doc uploads."
            );
          }

          navigate(`/MyFirstLand?category=agencies&newAgency=true`);
        } else {
          const errorMsg =
            response.data.message ||
            "Some error occurred while creating the agency!";
          toast.error(errorMsg);
        }
      } else {
        const errorMsg =
          response.data?.message ||
          "Some error occurred while trying to create the agency!";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        "Some error occurred while registering the agency. Please try again later!"
      );
    } finally {
      hideLoader();
    }
  };

  // ---------- Verify email (server call) ----------
  const verifyAgencyEmail = async (email) => {
    try {
      showLoader();
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/dash/pro-agencies/verify-email`,
        { email },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      return response;
    } catch (error) {
      toast.error(
        "Some error occurred while trying to verify the entered email"
      );
      console.error(error);
      return null;
    } finally {
      hideLoader();
    }
  };

  // ---------- Validations ----------
  const validateEmailDetails = () => {
    if (!emailDetails.email) {
      toast.error("Please provide your Email");
      return false;
    }
    if (!isValidEmail(emailDetails.email)) {
      toast.error("Please provide a valid Email format");
      return false;
    }
    return true;
  };

  const validateBasicDetails = () => {
    if (!basicDetails.name) {
      toast.error("Please provide your agency's Name");
      return false;
    }
    if (!basicDetails.phone) {
      toast.error("Please provide your agency's Phone Number");
      return false;
    }
    if (basicDetails.phone && !isValidPhone(basicDetails.phone)) {
      toast.error("Please provide a valid Phone Number");
      return false;
    }
    // license number now validated per-license
    return true;
  };

  const validateAllLicenses = () => {
    if (!licenseList.length) {
      toast.error("Please add at least one license.");
      return false;
    }
    for (let i = 0; i < licenseList.length; i++) {
      const lic = licenseList[i];
      if (!lic.licenseNumber) {
        toast.error(`License #${i + 1}: Please enter the license number.`);
        return false;
      }
      if (!lic.issuingState) {
        toast.error(`License #${i + 1}: Please select the issuing state.`);
        return false;
      }
      // issued/expiry optional; enforce if needed
    }
    return true;
  };

  const validateAddressDetails = () => {
    if (!addressDetails.street1) {
      toast.error("Please enter the street address of the agency");
      return false;
    }
    if (!addressDetails.city) {
      toast.error("Please provide the city");
      return false;
    }
    if (!addressDetails.state) {
      toast.error("Please select the state");
      return false;
    }
    if (!addressDetails.zipcode) {
      toast.error("Please provide the zipcode");
      return false;
    }
    if (!addressDetails.landmark) {
      toast.error("Please provide any nearby landmark");
      return false;
    }
    return true;
  };

  const validateProfessionalDetails = () => {
    if (!professionalDetails.languages) {
      toast.error("Please provide the languages that your agency can handle.");
      return false;
    }
    if (!professionalDetails.establishedYear) {
      toast.error("Please provide your agency's establishment year.");
      return false;
    }
    if (!professionalDetails.specialization) {
      toast.error(
        "Please provide your agency's specialization in real estate."
      );
      return false;
    }
    return true;
  };

  const handleResendCode = async () => {
    try {
      const response = await sendEmailVerification(emailDetails.email);
      if (
        response.status === 200 &&
        response.data &&
        "success" in response.data
      ) {
        startTimer();
      } else {
        toast.error("Failed to resend verification code. Please try again.");
      }
    } catch (error) {
      console.error("Error resending verification code:", error);
      toast.error("An error occurred while resending the verification code.");
    }
  };

  const validateVerificationDetails = () => {
    if (!verificationDetails.verificationCode) {
      toast.error("Please provide the verification code.");
      return false;
    }
    return true;
  };

  // ---------- resend code timer ----------
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(
        () => setTimer((prevTimer) => prevTimer - 1),
        1000
      );
    } else if (timer === 0) {
      setShowResendButton(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.container}>
        <h2 className={styles.title}>Agency Onboard</h2>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div
            className={`${styles.step} ${step >= 1 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 2 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 3 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 4 ? styles.active : ""}`}
          ></div>
          <div
            className={`${styles.step} ${step >= 5 ? styles.active : ""}`}
          ></div>
        </div>

        <div className={styles.formContainer}>
          {/* Email Step */}
          {step === 1 && (
            <div className={styles.formSection}>
              <div className={styles.formComponent}>
                <TextField
                  id="email"
                  name="email"
                  label="Email"
                  variant="outlined"
                  required
                  type="email"
                  fullWidth
                  value={emailDetails.email}
                  onChange={handleEmailChange}
                />
              </div>
              <div className={styles.formComponent}>
                <div className={styles.formAction}>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Basic Details + Multi-License Step */}
          {step === 2 && (
            <div className={styles.formSection}>
              {/* Logo uploader */}
              <div className={styles.formComponent}>
                <div className={styles.logoRow}>
                  <div className={styles.logoPreviewWrap}>
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className={styles.logoPreview}
                      />
                    ) : (
                      <div className={styles.logoPlaceholder}>Logo</div>
                    )}
                  </div>

                  <div className={styles.logoActions}>
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      style={{ display: "none" }}
                      onChange={onLogoChange}
                    />
                    <Button
                      variant="outlined"
                      onClick={() =>
                        document.getElementById("logo-input").click()
                      }
                    >
                      {logoFile ? "Change Logo" : "Upload Logo"}
                    </Button>
                    {logoFile && (
                      <Button
                        variant="text"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview("");
                        }}
                        className={styles.textButton}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Basic Agency fields */}
              <div className={styles.formComponent}>
                <TextField
                  id="name"
                  name="name"
                  label="Name of the Agency"
                  variant="outlined"
                  required
                  type="text"
                  fullWidth
                  value={basicDetails.name}
                  onChange={handleBasicChange}
                />
              </div>

              <div
                className={`${styles.formComponent} ${styles.componentType1}`}
              >
                <TextField
                  id="phone"
                  name="phone"
                  label="Phone Number"
                  variant="outlined"
                  type="number"
                  inputProps={{ maxLength: 10 }}
                  fullWidth
                  value={basicDetails.phone}
                  onChange={handleBasicChange}
                />
              </div>

              <div className={styles.formComponent}>
                <TextField
                  id="bio"
                  name="bio"
                  label="Bio"
                  multiline
                  rows={4}
                  value={basicDetails.bio}
                  onChange={handleBasicChange}
                />
              </div>

              {/* ===== Multi License Details ===== */}
              <div className={styles.formComponent} style={{ marginTop: 8 }}>
                <h3>License Details</h3>

                {licenseList.map((lic, idx) => (
                  <div
                    key={idx}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: 12,
                      marginTop: 12,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 8,
                      }}
                    >
                      <strong>License #{idx + 1}</strong>
                      {licenseList.length > 1 && (
                        <Button
                          color="error"
                          variant="text"
                          onClick={() => removeLicense(idx)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div
                      className={`${styles.formComponent} ${styles.componentType2}`}
                    >
                      <TextField
                        id={`licenseNumber-${idx}`}
                        name="licenseNumber"
                        label="License Number *"
                        variant="outlined"
                        required
                        type="text"
                        fullWidth
                        value={lic.licenseNumber}
                        onChange={(e) => handleLicenseField(idx, e)}
                      />
                      <TextField
                        id={`licenseType-${idx}`}
                        name="licenseType"
                        label="License Type (e.g., Brokerage)"
                        variant="outlined"
                        type="text"
                        fullWidth
                        value={lic.licenseType}
                        onChange={(e) => handleLicenseField(idx, e)}
                      />
                    </div>

                    <div
                      className={`${styles.formComponent} ${styles.componentType2}`}
                    >
                      <FormControl fullWidth required>
                        <InputLabel id={`issuing-state-label-${idx}`}>
                          Issuing State *
                        </InputLabel>
                        <Select
                          labelId={`issuing-state-label-${idx}`}
                          id={`issuingState-${idx}`}
                          name="issuingState"
                          label="Issuing State"
                          value={lic.issuingState}
                          onChange={(e) => handleLicenseField(idx, e)}
                        >
                          {stateOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel id={`license-status-label-${idx}`}>
                          Status
                        </InputLabel>
                        <Select
                          labelId={`license-status-label-${idx}`}
                          id={`status-${idx}`}
                          name="status"
                          label="Status"
                          value={lic.status}
                          onChange={(e) => handleLicenseField(idx, e)}
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Expired">Expired</MenuItem>
                          <MenuItem value="Suspended">Suspended</MenuItem>
                        </Select>
                      </FormControl>
                    </div>

                    <div
                      className={`${styles.formComponent} ${styles.componentType2}`}
                    >
                      <FormControl fullWidth>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            id={`issuedDate-${idx}`}
                            label="Issued Date"
                            value={lic.issuedDate}
                            onChange={(date) =>
                              handleIssuedDateChange(idx, date)
                            }
                          />
                        </LocalizationProvider>
                      </FormControl>

                      <FormControl fullWidth>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            id={`expiryDate-${idx}`}
                            label="Expiry Date"
                            value={lic.expiryDate}
                            onChange={(date) =>
                              handleExpiryDateChange(idx, date)
                            }
                            minDate={lic.issuedDate || undefined}
                          />
                        </LocalizationProvider>
                      </FormControl>
                    </div>

                    <div
                      className={`${styles.formComponent} ${styles.componentType2}`}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <input
                          id={`license-doc-input-${idx}`}
                          type="file"
                          accept="image/png, image/jpeg, image/webp, application/pdf"
                          style={{ display: "none" }}
                          onChange={(e) => onLicenseFileChange(idx, e)}
                        />
                        <Button
                          variant="outlined"
                          onClick={() =>
                            document
                              .getElementById(`license-doc-input-${idx}`)
                              .click()
                          }
                        >
                          {lic.file
                            ? "Change Document"
                            : "Upload License Document"}
                        </Button>
                        {lic.file && (
                          <Button
                            variant="text"
                            onClick={() =>
                              setLicenseList((prev) => {
                                const copy = [...prev];
                                copy[idx] = {
                                  ...copy[idx],
                                  file: null,
                                  fileName: "",
                                };
                                return copy;
                              })
                            }
                            className={styles.textButton}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      {lic.fileName && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {lic.fileName}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: 12 }}>
                  <Button variant="outlined" onClick={addLicense}>
                    + Add License
                  </Button>
                </div>
              </div>

              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button variant="outlined" size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Address Step */}
          {step === 3 && (
            <div className={styles.formSection}>
              <div className={styles.formSection}>
                <TextField
                  id="street1"
                  name="street1"
                  label="Street Address 1"
                  variant="outlined"
                  required
                  type="text"
                  fullWidth
                  value={addressDetails.street1}
                  onChange={handleAddressChange}
                />
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <TextField
                  id="street2"
                  name="street2"
                  label="Street Address 2"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={addressDetails.street2}
                  onChange={handleAddressChange}
                />
                <TextField
                  id="city"
                  name="city"
                  label="City"
                  variant="outlined"
                  required
                  type="text"
                  fullWidth
                  value={addressDetails.city}
                  onChange={handleAddressChange}
                />
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType3}`}
              >
                <FormControl fullWidth>
                  <InputLabel id="state-label">State *</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    name="state"
                    label="State"
                    value={addressDetails.state}
                    onChange={handleAddressChange}
                  >
                    {stateOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  id="zipcode"
                  name="zipcode"
                  label="Zipcode"
                  variant="outlined"
                  required
                  type="number"
                  fullWidth
                  value={addressDetails.zipcode}
                  onChange={handleAddressChange}
                />
                <TextField
                  id="landmark"
                  name="landmark"
                  label="Landmark Nearby"
                  variant="outlined"
                  required
                  type="text"
                  fullWidth
                  value={addressDetails.landmark}
                  onChange={handleAddressChange}
                />
              </div>

              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button variant="outlined" size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Professional Details Step */}
          {step === 4 && (
            <div className={styles.formSection}>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <LanguageSelector
                  professionalDetails={professionalDetails}
                  handleProfessionalChange={handleProfessionalChange}
                />
                <FormControl fullWidth>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      id="establishedYear"
                      name="establishedYear"
                      label="Established Year"
                      views={["year"]}
                      maxDate={dayjs()}
                      value={professionalDetails.establishedYear || null}
                      onChange={handleEstablishedYearChange}
                    />
                  </LocalizationProvider>
                </FormControl>
              </div>
              <div
                className={`${styles.formComponent} ${styles.componentType2}`}
              >
                <FormControl fullWidth required>
                  <InputLabel id="specialization-label">
                    Specialization
                  </InputLabel>
                  <Select
                    labelId="specialization-label"
                    id="specialization"
                    name="specialization"
                    multiple
                    value={professionalDetails.specialization
                      .split(", ")
                      .filter(Boolean)}
                    onChange={handleProfessionalChange}
                    renderValue={(selected) => (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                      >
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </div>
                    )}
                  >
                    {specializations.map((spec) => (
                      <MenuItem key={spec} value={spec}>
                        {spec}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="text"
                  onClick={handleClickOpen}
                  className={styles.textButton}
                >
                  Detailed specialization information
                </Button>
              </div>

              <h3 className="mar-t-40">Public Social Links:</h3>
              <div
                className={`${styles.formComponent} ${styles.componentType1}`}
              >
                <TextField
                  id="linkedinLink"
                  name="linkedinLink"
                  label="LinkedIn Link"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={professionalDetails.linkedinLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="instagramLink"
                  name="instagramLink"
                  label="Instagram Link"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={professionalDetails.instagramLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="youtubeLink"
                  name="youtubeLink"
                  label="YouTube Link"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={professionalDetails.youtubeLink}
                  onChange={handleProfessionalChange}
                />
                <TextField
                  id="tiktokLink"
                  name="tiktokLink"
                  label="Tiktok Link"
                  variant="outlined"
                  type="text"
                  fullWidth
                  value={professionalDetails.tiktokLink}
                  onChange={handleProfessionalChange}
                />
              </div>

              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button variant="outlined" size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button variant="contained" size="large" onClick={handleNext}>
                    Next
                  </Button>
                </div>
              </div>

              <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                  {"Detailed specialization information"}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {/* … keep your detailed list exactly as before … */}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          )}

          {/* Verification Step */}
          {step === 5 && (
            <div className={styles.formSection}>
              <div>Email Verification</div>
              <div
                className={`${styles.formComponent} ${styles.componentType1}`}
              >
                <TextField
                  id="verificationCode"
                  name="verificationCode"
                  label="Verification Code"
                  variant="outlined"
                  type="text"
                  fullWidth
                  required
                  value={verificationDetails.verificationCode}
                  onChange={handleVerificationChange}
                />
                <div>
                  {showResendButton ? (
                    <Button onClick={handleResendCode}>Resend Code</Button>
                  ) : (
                    <div>You can resend code after {timer} seconds</div>
                  )}
                </div>
              </div>

              <div className={styles.formComponent}>
                <div className={styles.formActions}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <CenteredDialog
          open={openExistingAgencyModal}
          onClose={handleExistingAgencyModalClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <CenteredDialogTitle id="alert-dialog-title">
            Email Already Exists
          </CenteredDialogTitle>
          <CenteredDialogContent>
            <DialogContentText id="alert-dialog-description">
              A user/agency account is already registered with the email
              address:
            </DialogContentText>
            <DialogContentText>
              <strong>{emailDetails.email}</strong>
            </DialogContentText>
            <DialogContentText>
              Would you like to use another email to open an agency account?
            </DialogContentText>
          </CenteredDialogContent>
          <CenteredDialogActions>
            <Button
              onClick={handleDashAgenciesRedirect}
              variant="outlined"
              sx={{ marginRight: 2 }}
            >
              Show My Agencies
            </Button>
            <Button
              onClick={handleUserAnotherEmail}
              variant="contained"
              autoFocus
            >
              Yes
            </Button>
          </CenteredDialogActions>
        </CenteredDialog>
      </div>
      <Footer />
    </div>
  );
}

export default DashAddAgency;
